import { z } from 'zod'

import { EnumBuilder, type ZodInfer } from '@mntn-dev/utility-types'

import { ModelFilterSchema } from '../model-filter-schema.ts'
import { FolderUrnSchema } from './file.models.ts'
import { EmailAddressSchema } from './property.models.ts'
import { DocumentIdSchema } from './unique-id.models.ts'

export const [DocumentTemplates, DocumentTemplateSchema, DocumentTemplateEnum] =
  EnumBuilder('quickframe-sow')

export type DocumentTemplate = ZodInfer<typeof DocumentTemplateSchema>

export const DocumentTemplateOverridesSchema = z
  .object({
    primaryColor: z.string(),
    accentColor: z.string(),
    logoPath: z.string(),
    companyName: z.string(),
    companyAddress: z
      .object({
        line1: z.string(),
        line2: z.string(),
        city: z.string(),
        state: z.string(),
        postalCode: z.string(),
        country: z.string(),
      })
      .partial(),
    footerText: z.string(),
  })
  .partial()

const ProjectSpecificFeeSchema = z.object({
  description: z.string(),
  fee: z.number(),
  broadcastNote: z.string().optional(),
})

export const QuickframeSowDocumentPayloadSchema = z
  .object({
    templateId: z.literal('quickframe-sow').optional(),
    clientName: z.string(),
    brand: z.string().optional(),
    platformOrPartner: z.string().optional(),
    clientContact: z.string().optional(),
    clientEmail: EmailAddressSchema.optional(),
    clientPhone: z.string().optional(),
    bidAcceptanceDate: z.string().optional(),
    customerProjectDueDate: z.string().optional(),
    basePackage: z.object({
      packageName: z.string(),
      fee: z.number(),
      credits: z.number().optional(),
    }),
    preProductionServices: z.array(z.string()).optional(),
    postProductionDeliverables: z.array(z.string()).optional(),
    addOnPreProductionServices: z.array(z.string()).optional(),
    addOnPostProductionDeliverables: z.array(z.string()).optional(),
    totalCostDollars: z.number().optional(),
    totalCostCredits: z.number().optional(),
    basePackageCredits: z.number().optional(),
    addOnsCredits: z.number().optional(),
    creditProgramName: z.string().optional(),
    selectedPaymentMethod: z
      .enum(['ach', 'stripe', 'ams', 'credits'])
      .optional(),
    projectSpecificFees: z.array(ProjectSpecificFeeSchema).optional(),
    totalFees: z.number(),
    paymentMethod: z
      .object({
        ach: z.boolean().optional(),
        creditCard: z.boolean().optional(),
        clientBillingPortal: z.boolean().optional(),
      })
      .optional(),
    billingContactName: z.string().optional(),
    billingContactEmail: EmailAddressSchema.optional(),
    billingAddress: z.string().optional(),
    accountsPayableEmail: EmailAddressSchema.optional(),
    templateOverrides: DocumentTemplateOverridesSchema.optional(),
  })
  .refine(
    (data) => {
      const addOnTotal =
        data.projectSpecificFees?.reduce((sum, fee) => sum + fee.fee, 0) ?? 0
      const basePackageFee = data.basePackage.fee
      return Math.abs(basePackageFee + addOnTotal - data.totalFees) < 0.01
    },
    {
      path: ['totalFees'],
      error: 'totalFees must equal base package fee plus project specific fees',
    }
  )

export type QuickframeSowDocumentPayload = ZodInfer<
  typeof QuickframeSowDocumentPayloadSchema
>

export const DocumentDomainSelectModelSchema = z.object({
  documentId: DocumentIdSchema,
  folderUrn: FolderUrnSchema,
  template: DocumentTemplateSchema,
  version: z.int().positive(),
  payload: QuickframeSowDocumentPayloadSchema,
})

export type DocumentDomainSelectModel = ZodInfer<
  typeof DocumentDomainSelectModelSchema
>

export const DocumentDomainInsertModelSchema = z.object({
  documentId: DocumentIdSchema.optional(),
  folderUrn: FolderUrnSchema,
  template: DocumentTemplateSchema,
  version: z.int().positive().prefault(1),
  payload: QuickframeSowDocumentPayloadSchema,
})

export type DocumentDomainInsertModel = ZodInfer<
  typeof DocumentDomainInsertModelSchema
>

export const DocumentDomainUpdateModelSchema = z
  .object({
    folderUrn: FolderUrnSchema,
    template: DocumentTemplateSchema,
    version: z.int().positive(),
    payload: QuickframeSowDocumentPayloadSchema,
  })
  .partial()

export type DocumentDomainUpdateModel = ZodInfer<
  typeof DocumentDomainUpdateModelSchema
>

export const DocumentDomainSelectModelFiltersSchema = z
  .object({
    documentId: ModelFilterSchema(DocumentIdSchema),
    folderUrn: ModelFilterSchema(FolderUrnSchema),
    template: ModelFilterSchema(DocumentTemplateSchema),
  })
  .partial()

export type DocumentDomainSelectModelFilters = ZodInfer<
  typeof DocumentDomainSelectModelFiltersSchema
>
